import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Logo from "../components/Logo";
import Achievements from "../components/Achievements";
import LiveDemoHero from "../components/LiveDemoHero";
import { useProfile } from "../context/ProfileContext";
import { getDailyStory, BACKEND } from "../lib/api";
import { MessageSquare, BookOpen, ListChecks, Map, ArrowRight, Sparkles, Compass, Lightbulb, Flame, Briefcase, Sun } from "lucide-react";

const modes = [
  { to: "/chat", title: "Chat Tutor", desc: "Ask anything in mythology or STEM. Warm, story-driven answers.", icon: MessageSquare, testid: "mode-chat" },
  { to: "/stories", title: "Storytelling", desc: "Timeless tales from world mythology with a moral, plus AI illustrations.", icon: BookOpen, testid: "mode-stories" },
  { to: "/quiz", title: "Quiz Me", desc: "3–10 MCQs on any topic, at your level. Share with friends.", icon: ListChecks, testid: "mode-quiz" },
  { to: "/paths", title: "Learning Paths", desc: "A 6-step guided journey: explainer → story → questions.", icon: Map, testid: "mode-paths" },
];

const pillars = [
  { icon: Lightbulb, title: "Content", text: "Stories, analogies, and explainers in your language." },
  { icon: Sparkles, title: "Gaming", text: "Quizzes & streaks that spark curiosity daily." },
  { icon: Compass, title: "Exploration", text: "50 STEM models + world mythology to guide your journey." },
];

export default function Home() {
  const { profile } = useProfile();
  const greeting = profile.name ? `Welcome back, ${profile.name}` : "Welcome to EduSphere";
  const [daily, setDaily] = useState(null);

  useEffect(() => {
    // Don't auto-trigger generation on first ever visit (could be slow); just probe.
    // We use a HEAD-like behaviour by allowing a 30s timeout below. If it's not cached, the user can click "Read today's story" which routes to /today.
    getDailyStory().then(setDaily).catch(() => {});
  }, []);

  const dailyImg = daily?.illustration_url ? `${BACKEND}${daily.illustration_url}` : null;

  return (
    <div className="grain" data-testid="home-page">
      {/* LIVE DEMO HERO */}
      <LiveDemoHero />

      {/* Personalised stat strip */}
      {profile.onboarded && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pb-10" data-testid="my-stats-strip">
          <div className="card p-5 md:p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div data-testid="my-streak">
              <div className="flex items-center gap-2 text-[#E07A5F] text-xs font-semibold uppercase tracking-[0.18em]">
                <Flame size={14} /> Daily streak
              </div>
              <div className="font-heading font-extrabold text-2xl text-[#1A1A1A] mt-1">{profile.streak || 0} days</div>
            </div>
            <div data-testid="my-stories">
              <div className="text-[#3A5A40] text-xs font-semibold uppercase tracking-[0.18em]">Stories read</div>
              <div className="font-heading font-extrabold text-2xl text-[#1A1A1A] mt-1">{profile.storiesRead || 0}</div>
            </div>
            <div data-testid="my-quizzes">
              <div className="text-[#3A5A40] text-xs font-semibold uppercase tracking-[0.18em]">Quizzes taken</div>
              <div className="font-heading font-extrabold text-2xl text-[#1A1A1A] mt-1">{profile.quizzesTaken || 0}</div>
            </div>
            <div data-testid="my-best">
              <div className="text-[#3A5A40] text-xs font-semibold uppercase tracking-[0.18em]">Best quiz</div>
              <div className="font-heading font-extrabold text-2xl text-[#1A1A1A] mt-1">{profile.bestQuiz || 0}%</div>
            </div>
          </div>
        </section>
      )}

      {/* EDUSPHERE XR PROMO BAND — flagship immersive capability */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="home-xr-band">
        <Link to="/xr" className="block group">
          <div className="relative rounded-2xl p-8 md:p-14 overflow-hidden border border-[#C9A66B]/50"
               style={{ background: "linear-gradient(135deg, #0E0F12 0%, #1a0f08 50%, #6b3418 110%)" }}>
            <div className="relative z-10 grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <div className="text-[11px] tracking-[0.28em] uppercase mythos-gold font-bold mb-3 flex items-center gap-2">
                  <Sparkles size={12} /> New · Immersive learning
                </div>
                <h2 className="font-mythos text-4xl md:text-6xl font-extrabold tracking-tight leading-[1.02] text-[#F4EFE6]">
                  Enter the story. <br/><span className="mythos-gold">Experience the knowledge.</span>
                </h2>
                <p className="text-[#E8EBF0]/75 mt-5 max-w-xl leading-relaxed text-lg">
                  EduSphere XR · Kurukshetra. An interactive 3D world where story, strategy, culture and knowledge meet.
                </p>
                <div className="mt-7 flex flex-wrap gap-2">
                  {["3D battlefield", "AI Guide", "The Decision", "Knowledge challenge"].map((t) => (
                    <span key={t} className="text-[10px] tracking-[0.14em] uppercase font-bold mythos-gold border border-[#C9A66B]/40 rounded-full px-2.5 py-1">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full px-7 py-4 font-bold whitespace-nowrap group-hover:translate-x-1 transition text-base" data-testid="home-xr-cta">
                Enter EduSphere XR <ArrowRight size={18} />
              </div>
            </div>
            <div className="absolute -right-32 -top-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, #C9A66B 0%, transparent 60%)", opacity: 0.3 }} />
            <div className="absolute -right-20 -bottom-20 w-72 h-72 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, #c08252 0%, transparent 70%)", opacity: 0.4 }} />
          </div>
        </Link>
      </section>

      {/* IMMERSIVE LEARNING LAB — flagship VC-ready demonstrator */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="home-immersive-band">
        <Link to="/immersive" className="block group">
          <div className="rounded-2xl p-8 md:p-12 relative overflow-hidden border transition hover:border-[#7CE4FF]"
               style={{ background: "linear-gradient(135deg, #0C1E2A 0%, #050810 100%)", borderColor: "#7CE4FF44" }}>
            <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center relative z-10">
              <div>
                <div className="text-[11px] tracking-[0.24em] uppercase font-bold mb-3 flex items-center gap-2" style={{ color: "#7CE4FF" }}>
                  <Sparkles size={12} /> New · Immersive Lab
                </div>
                <h2 className="font-mythos text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.04] text-[#F4EFE6]">
                  Don't read about it. <span style={{ color: "#7CE4FF" }}>Become it.</span>
                </h2>
                <p className="text-[#E8EBF0]/75 mt-4 max-w-xl leading-relaxed">
                  Five interactive worlds, one reusable engine. Become a data packet, enter a human cell, fly an aircraft, operate a reactor, break an AI.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Data Packet · LIVE", "Human Cell", "Aircraft", "Power Plant", "Break the AI"].map((t) => (
                    <span key={t} className="text-[10px] tracking-[0.14em] uppercase font-bold border rounded-full px-2.5 py-1" style={{ color: "#7CE4FF", borderColor: "#7CE4FF55" }}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 rounded-full px-6 py-3 font-semibold whitespace-nowrap group-hover:translate-x-1 transition text-[#0E0F12]" style={{ background: "#7CE4FF" }} data-testid="home-immersive-cta">
                  Enter the lab <ArrowRight size={16} />
                </div>
                <Link to="/immersive/future" className="flex items-center gap-2 rounded-full px-6 py-3 font-semibold whitespace-nowrap border transition hover:bg-[#7CE4FF] hover:text-[#0E0F12] text-center justify-center" style={{ color: "#7CE4FF", borderColor: "#7CE4FF" }} data-testid="home-immersive-future-cta">
                  Show me the future
                </Link>
              </div>
            </div>
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #7CE4FF 0%, transparent 70%)" }} />
            <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{ background: "radial-gradient(circle, #E07AFF 0%, transparent 70%)" }} />
          </div>
        </Link>
      </section>

      {/* BHARAT QUEST PROMO BAND — gateway to gaming category */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="home-bharat-quest-band">
        <Link to="/bharat-quest" className="block group">
          <div className="mythos-ink-bg rounded-2xl p-8 md:p-12 relative overflow-hidden border border-[#2A3349] transition hover:border-[#C9A66B]">
            <div className="grid md:grid-cols-[1fr_auto] gap-8 items-center relative z-10">
              <div>
                <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3 flex items-center gap-2">
                  <Sparkles size={12} /> New · Gaming
                </div>
                <h2 className="font-mythos text-3xl md:text-5xl font-extrabold tracking-tight leading-[1.04] text-[#F4EFE6]">
                  Play through a <span className="mythos-gold">civilization.</span>
                </h2>
                <p className="text-[#E8EBF0]/75 mt-4 max-w-xl leading-relaxed">
                  8 regions to unlock. 6 legends to converse with. Daily streak mystery, global leaderboard,
                  shareable Cultural Intelligence Score. Civilizational learning, gamified.
                </p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Daily Quest", "Civilization Map", "Talk to Chanakya", "Global Ranks"].map((t) => (
                    <span key={t} className="text-[10px] tracking-[0.14em] uppercase font-bold mythos-gold border border-[#3A3D44] rounded-full px-2.5 py-1">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-semibold whitespace-nowrap group-hover:translate-x-1 transition" data-testid="home-bharat-quest-cta">
                Enter the quest <ArrowRight size={16} />
              </div>
            </div>
            {/* Decorative gold orb */}
            <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-30 pointer-events-none" style={{ background: "radial-gradient(circle, #C9A66B 0%, transparent 70%)" }} />
          </div>
        </Link>
      </section>

      {/* TODAY'S STORY HERO */}
      {daily && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="home-today-section">
          <div className="card overflow-hidden grid md:grid-cols-[1fr_360px] gap-0">
            <div className="p-7 md:p-10">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#E07A5F] font-semibold flex items-center gap-1.5">
                <Sun size={12} /> Story of the day
              </div>
              <h2 className="font-story text-3xl md:text-4xl font-semibold text-[#1A1A1A] leading-tight mt-2" data-testid="home-today-title">{daily.title}</h2>
              <p className="text-[#4A4A4A] mt-3 line-clamp-3 max-w-xl">{daily.story.slice(0, 220)}…</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/today" className="btn-primary inline-flex items-center gap-2" data-testid="home-today-cta">
                  Read today's story <ArrowRight size={14} />
                </Link>
                <span className="badge">{daily.topic}</span>
              </div>
            </div>
            {dailyImg && (
              <div className="hidden md:block bg-[#FBE7DF]">
                <img src={dailyImg} alt={daily.title} className="w-full h-full object-cover" data-testid="home-today-illustration" />
              </div>
            )}
          </div>
        </section>
      )}

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-14" data-testid="pillars-section">
        <div className="grid md:grid-cols-3 gap-5">
          {pillars.map((p) => (
            <div key={p.title} className="card p-6 flex gap-4 items-start" data-testid={`pillar-${p.title.toLowerCase()}`}>
              <div className="w-11 h-11 rounded-xl bg-[#FBE7DF] grid place-items-center text-[#E07A5F]">
                <p.icon size={22} />
              </div>
              <div>
                <div className="font-heading font-semibold text-lg text-[#1A1A1A]">{p.title}</div>
                <div className="text-sm text-[#4A4A4A] mt-1">{p.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* MODES */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="modes-section">
        <div className="flex items-end justify-between mb-7 flex-wrap gap-3">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Pick a mode</div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">How would you like to learn today?</h2>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {modes.map((m) => (
            <Link to={m.to} key={m.to} data-testid={m.testid} className="card p-6 group block">
              <div className="w-12 h-12 rounded-xl bg-[#EAECE6] grid place-items-center text-[#3A5A40] mb-5 group-hover:bg-[#FBE7DF] group-hover:text-[#E07A5F] transition">
                <m.icon size={22} />
              </div>
              <div className="font-heading font-semibold text-xl text-[#1A1A1A]">{m.title}</div>
              <div className="text-sm text-[#4A4A4A] mt-2 leading-relaxed">{m.desc}</div>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#E07A5F]">
                Open <ArrowRight size={14} />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Achievements (only when onboarded) */}
      {profile.onboarded && (
        <section className="max-w-7xl mx-auto px-5 md:px-10 pb-12" data-testid="home-achievements-section">
          <Achievements compact />
        </section>
      )}

      {/* Careers CTA */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-20" data-testid="careers-cta-section">
        <div className="card p-7 md:p-10 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">For college &amp; youth</div>
            <h3 className="font-heading text-2xl md:text-3xl font-bold mt-1">Turn what you learn into a career.</h3>
            <p className="text-[#4A4A4A] mt-2 max-w-2xl">Six pathways — from animator to renewable-energy technician — each with the skills, EduSphere models, and a starter project to get going.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/careers" className="btn-primary inline-flex items-center gap-2" data-testid="home-careers-cta">
              <Briefcase size={16} /> Explore careers
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
